import { fmtINR } from '../../lib/currency';
import { useCallback, useEffect, useState } from 'react';
import {
  amazonadsintelApi,
  type AdRecommendation,
  type AdsDashboard,
  type BudgetForecast,
  type CampaignSummary,
  type ExecutionRecord,
  type ExperimentRecord,
  type KeywordIntel,
  type SearchTermIntel,
} from '../../api/amazonadsintel';

type Tab = 'overview' | 'campaigns' | 'keywords' | 'search-terms' | 'recommendations' | 'experiments' | 'forecasts' | 'execution';


function healthClass(h: string) {
  if (h === 'good') return 'text-emerald-700 bg-emerald-50';
  if (h === 'fair') return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
}

export function AmazonAdvertisingIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<AdsDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [keywords, setKeywords] = useState<KeywordIntel[]>([]);
  const [searchTerms, setSearchTerms] = useState<SearchTermIntel[]>([]);
  const [recommendations, setRecommendations] = useState<AdRecommendation[]>([]);
  const [forecasts, setForecasts] = useState<BudgetForecast[]>([]);
  const [experiments, setExperiments] = useState<ExperimentRecord[]>([]);
  const [executions, setExecutions] = useState<ExecutionRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, camps, kws, sts, recs, fc, exps, execs] = await Promise.all([
        amazonadsintelApi.dashboard(),
        amazonadsintelApi.listCampaigns(),
        amazonadsintelApi.listKeywords(),
        amazonadsintelApi.listSearchTerms(),
        amazonadsintelApi.listRecommendations(),
        amazonadsintelApi.listForecasts(),
        amazonadsintelApi.listExperiments(),
        amazonadsintelApi.listExecutions(),
      ]);
      setDashboard(dash);
      setCampaigns(camps);
      setKeywords(kws);
      setSearchTerms(sts);
      setRecommendations(recs);
      setForecasts(fc);
      setExperiments(exps);
      setExecutions(execs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load advertising intelligence');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (id: string) => {
    setBusy(true);
    try { await amazonadsintelApi.approveRecommendation(id); await refresh(); }
    finally { setBusy(false); }
  };

  const execute = async (id: string) => {
    setBusy(true);
    try { await amazonadsintelApi.startExecution(id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Execution failed — approval may be required'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Advertising Dashboard' },
    { id: 'campaigns', label: 'Campaign Workspace' },
    { id: 'keywords', label: 'Keyword Intelligence' },
    { id: 'search-terms', label: 'Search Term Explorer' },
    { id: 'recommendations', label: 'Recommendation Center' },
    { id: 'experiments', label: 'Experiment Center' },
    { id: 'forecasts', label: 'Forecast Console' },
    { id: 'execution', label: 'Execution Monitor' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Amazon Advertising Intelligence</h1>
          <p className="text-sm text-slate-500">Campaign Copilot, bid &amp; budget optimization — BCP-00002</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-violet-100 text-violet-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Spend', value: fmtINR(dashboard.total_spend) },
            { label: 'Total Sales', value: fmtINR(dashboard.total_sales) },
            { label: 'ROAS', value: dashboard.overall_roas.toFixed(2) },
            { label: 'ACOS', value: `${dashboard.overall_acos.toFixed(1)}%` },
            { label: 'Opportunity Score', value: dashboard.opportunity_score.toFixed(0) },
            { label: 'Savings Potential', value: fmtINR(dashboard.savings_potential) },
            { label: 'Growth Potential', value: fmtINR(dashboard.growth_potential) },
            { label: 'Active Campaigns', value: dashboard.active_campaigns },
            { label: 'Campaign Health', value: `${dashboard.campaign_health_score.toFixed(0)}%` },
            { label: 'Budget Health', value: `${dashboard.budget_health_score.toFixed(0)}%` },
            { label: 'Keyword Health', value: `${dashboard.keyword_health_score.toFixed(0)}%` },
            { label: 'Recommendations', value: dashboard.active_recommendations },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Campaign</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Health</th>
                <th className="px-4 py-3">Spend</th>
                <th className="px-4 py-3">ROAS</th>
                <th className="px-4 py-3">ACOS</th>
                <th className="px-4 py-3">Score</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">{c.type.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${healthClass(c.health)}`}>{c.health}</span></td>
                  <td className="px-4 py-3">{fmtINR(c.metrics.spend)}</td>
                  <td className="px-4 py-3">{c.metrics.roas.toFixed(2)}</td>
                  <td className="px-4 py-3">{c.metrics.acos.toFixed(1)}%</td>
                  <td className="px-4 py-3">{c.opportunity_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'keywords' && (
        <div className="space-y-3">
          {keywords.map((k) => (
            <div key={k.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <p className="font-medium">{k.keyword} <span className="text-xs text-slate-400">({k.match_type})</span></p>
                  <p className="text-sm text-slate-600">Bid ${k.bid.toFixed(2)}{k.suggested_bid ? ` → $${k.suggested_bid.toFixed(2)}` : ''}</p>
                  {k.recommendation && <p className="mt-1 text-xs text-violet-700">{k.recommendation}</p>}
                </div>
                <div className="text-right text-sm">
                  <p>ROAS {k.metrics.roas.toFixed(2)} · ACOS {k.metrics.acos.toFixed(1)}%</p>
                  <p className="text-xs text-slate-500">{k.cluster} · {k.intent}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'search-terms' && (
        <div className="space-y-3">
          {searchTerms.map((st) => (
            <div key={st.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-medium">{st.search_term}</p>
              <p className="text-xs text-slate-500 capitalize">{st.classification} · {st.action}</p>
              <p className="mt-1 text-sm">Spend {fmtINR(st.metrics.spend)} · ROAS {st.metrics.roas.toFixed(2)} · CVR {st.metrics.cvr.toFixed(1)}%</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <span className="text-xs text-violet-600">#{r.priority_rank} · {r.type.replace(/_/g, ' ')}</span>
                  <p className="font-medium">{r.title}</p>
                  <p className="text-sm text-slate-600">{r.description}</p>
                  {r.evidence && <p className="mt-1 text-xs text-slate-500">{r.evidence}</p>}
                </div>
                <div className="flex flex-col gap-2 text-right text-sm shrink-0">
                  {r.expected_savings ? <p className="text-emerald-700">Save {fmtINR(r.expected_savings)}</p> : null}
                  {r.expected_growth ? <p className="text-violet-700">Grow {fmtINR(r.expected_growth)}</p> : null}
                  <p className="text-xs">{(r.confidence_score * 100).toFixed(0)}% confidence</p>
                  <div className="flex gap-2 justify-end">
                    {r.requires_approval && r.status === 'pending' && (
                      <button type="button" onClick={() => approve(r.id)} disabled={busy}
                        className="rounded-lg bg-violet-600 px-3 py-1 text-xs text-white hover:bg-violet-700 disabled:opacity-50">Approve</button>
                    )}
                    <button type="button" onClick={() => execute(r.id)} disabled={busy}
                      className="rounded-lg border border-violet-300 px-3 py-1 text-xs text-violet-700 hover:bg-violet-50 disabled:opacity-50">Execute</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="space-y-4">
          <button type="button" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              await amazonadsintelApi.createExperiment({ name: 'Bid Test - Exact Match', type: 'bid', hypothesis: 'Higher bid improves impression share' });
              await refresh();
            } finally { setBusy(false); }
          }}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
            New Bid Experiment
          </button>
          {experiments.length === 0 ? (
            <p className="text-sm text-slate-500">No experiments yet</p>
          ) : experiments.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-medium">{e.name}</p>
              <p className="text-xs text-slate-500">{e.type} · {e.status}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-slate-500">{f.period} forecast</p>
              <p className="mt-1 text-lg font-semibold">Spend {fmtINR(f.expected_spend)} → Sales {fmtINR(f.expected_sales)}</p>
              <p className="text-sm text-slate-600">ROAS {f.expected_roas.toFixed(2)}{f.growth_pct ? ` · +${f.growth_pct}%` : ''}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'execution' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Workflow</th><th className="px-4 py-3">Governance</th></tr>
            </thead>
            <tbody>
              {executions.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No executions yet</td></tr>
              ) : executions.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="px-4 py-3">{e.title}</td>
                  <td className="px-4 py-3">{e.status}</td>
                  <td className="px-4 py-3 text-xs">{e.workflow_id ?? '—'}</td>
                  <td className="px-4 py-3">{e.governance_approved ? 'Approved' : 'Pending'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
